import React, { createContext, FC, ReactNode, useCallback, useContext, useMemo } from 'react';
import { FilterSetting } from '../../store/api';
import { useFilterContextForCategory } from './FilterContext';
import { AggregationBucket, AggregationEntry } from '../../../models/Aggregations';

export const CUSTOM_FILTER_KEY = '$$CUSTOM';

const emptyBucket: AggregationBucket = {
    buckets: [],
    custom: false,
    customIndex: 0,
    docCount: 0,
    key: '',
    state: '',
    value: ''
};

const emptyAggregations: AggregationEntry = {
    buckets: [],
    more: false,
    quicksearchQuery: '',
    value: ''
};

type FilterGroupContext = {
    readonly category: string;
    readonly filter: FilterSetting;
    readonly aggregation: AggregationEntry;
    readonly quicksearchQuery: string;
    readonly changeFilter: (
        category: string,
        includeFilterValues: ReadonlyArray<string>,
        excludeFilterValues: ReadonlyArray<string>
    ) => void;
    readonly isSearching: boolean;
};

const Context = createContext<FilterGroupContext>({
    category: '',
    filter: {},
    aggregation: emptyAggregations,
    quicksearchQuery: '',
    changeFilter: () => {},
    isSearching: false
});

type FilterGroupContextProps = {
    children: ReactNode;

    readonly category: string;
    readonly isSearching?: boolean;
    readonly quicksearchQuery?: string;
    readonly quicksearchAggregations?: AggregationEntry;
};

export const FilterGroupContextProvider: FC<FilterGroupContextProps> = ({
    category,
    isSearching = false,
    quicksearchQuery = '',
    quicksearchAggregations,
    children
}) => {
    const { changeFilter, aggregation, filter } = useFilterContextForCategory(category);

    const value = useMemo<FilterGroupContext>(() => {
        const consideredAggregation = quicksearchAggregations ? quicksearchAggregations : aggregation;
        return {
            category,
            filter,
            aggregation: consideredAggregation,
            changeFilter,
            quicksearchQuery,
            isSearching
        };
    }, [quicksearchAggregations, aggregation, category, filter, changeFilter, quicksearchQuery, isSearching]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
};

type UseFilterGroupContext = {
    readonly category: string;
    readonly quicksearchQuery: string;
    readonly filter: FilterSetting;
    readonly bucket: AggregationBucket;
    readonly setFilter: (state: '' | 'include' | 'exclude', customValue?: string) => void;
    readonly isSearching: boolean;
};

export const useFilterGroupContext = (
    filterKey: string,
    filterSubKey?: string,
    customIndex: number = 0
): UseFilterGroupContext => {
    const ctx = useContext(Context);

    const isSubEntry = !!filterSubKey;

    const bucket =
        ctx.aggregation.buckets?.find(
            b => b.key === filterKey || (filterKey === CUSTOM_FILTER_KEY && b.custom && b.customIndex === customIndex)
        ) ?? emptyBucket;
    const subBucket = isSubEntry ? bucket.buckets.find(b => b.key === filterSubKey) : undefined;

    const setFilter = useCallback(
        (state: '' | 'include' | 'exclude', customValue?: string) => {
            let includeFilterValues = ctx.filter.includeFilterValues ? [...ctx.filter.includeFilterValues] : [];
            let excludeFilterValues = ctx.filter.excludeFilterValues ? [...ctx.filter.excludeFilterValues] : [];

            if (filterKey === CUSTOM_FILTER_KEY) {
                setCustomFilter(ctx, includeFilterValues, excludeFilterValues, state, customValue, customIndex);
                return;
            }

            const isFilterKeySelected =
                includeFilterValues.includes(filterKey) || excludeFilterValues.includes(filterKey);

            // Set the state of the children
            if (!isSubEntry) {
                bucket.buckets.forEach(sub => {
                    const subState = includeFilterValues.includes(sub.key)
                        ? 'include'
                        : excludeFilterValues.includes(sub.key)
                        ? 'exclude'
                        : '';
                    if (isFilterKeySelected || subState !== state) {
                        [includeFilterValues, excludeFilterValues] = setDefaultFilter(
                            includeFilterValues,
                            excludeFilterValues,
                            sub.key,
                            state
                        );
                    }
                });
            }

            // If we are a child, always remove the parent filter
            if (isSubEntry && isFilterKeySelected) {
                [includeFilterValues, excludeFilterValues] = setDefaultFilter(
                    includeFilterValues,
                    excludeFilterValues,
                    bucket.key,
                    ''
                );
            }

            const consideredKey = isSubEntry ? filterSubKey! : filterKey;

            [includeFilterValues, excludeFilterValues] = setDefaultFilter(
                includeFilterValues,
                excludeFilterValues,
                consideredKey,
                state
            );

            ctx.changeFilter(ctx.category, includeFilterValues, excludeFilterValues);
        },
        [ctx, filterKey, isSubEntry, filterSubKey, bucket.buckets, bucket.key, customIndex]
    );

    return {
        category: ctx.category,
        filter: ctx.filter,
        bucket: subBucket ? subBucket : bucket,
        quicksearchQuery: ctx.quicksearchQuery,
        setFilter,
        isSearching: ctx.isSearching
    };
};

export function setDefaultFilter(
    includeFilterValues: ReadonlyArray<string>,
    excludeFilterValues: ReadonlyArray<string>,
    key: string,
    state: '' | 'include' | 'exclude'
): [Array<string>, Array<string>] {
    const newIncludeFilterValues = [...includeFilterValues];
    const newExcludeFilterValues = [...excludeFilterValues];

    if (newIncludeFilterValues.includes(key)) {
        newIncludeFilterValues.splice(newIncludeFilterValues.indexOf(key), 1);
    } else if (state === 'include') {
        newIncludeFilterValues.push(key);
    }

    if (newExcludeFilterValues.includes(key)) {
        newExcludeFilterValues.splice(newExcludeFilterValues.indexOf(key), 1);
    } else if (state === 'exclude') {
        newExcludeFilterValues.push(key);
    }

    return [newIncludeFilterValues, newExcludeFilterValues];
}

function setCustomFilter(
    ctx: FilterGroupContext,
    includeFilterValues: string[],
    excludeFilterValues: string[],
    state: '' | 'include' | 'exclude',
    customValue?: string,
    customIndex: number = 0
) {
    if (!customValue) {
        return;
    }

    const cleanedIncludeFilterValues = includeFilterValues.filter(key =>
        isKeyNotCustomBucket(key, ctx.aggregation?.buckets, customIndex)
    );
    const cleanedExcludeFilterValues = excludeFilterValues.filter(key =>
        isKeyNotCustomBucket(key, ctx.aggregation?.buckets, customIndex)
    );

    if (state === 'include' && !includeFilterValues.includes(customValue)) {
        cleanedIncludeFilterValues.push(customValue);
    }
    if (state === 'exclude' && !excludeFilterValues.includes(customValue)) {
        cleanedExcludeFilterValues.push(customValue);
    }

    ctx.changeFilter(ctx.category, cleanedIncludeFilterValues, cleanedExcludeFilterValues);
}

function isKeyNotCustomBucket(
    key: string,
    buckets: ReadonlyArray<AggregationBucket> = [],
    customIndex: number = 0
): boolean {
    return buckets.some(b => b.key === key && !(b.custom && b.customIndex === customIndex));
}
